import styled from 'styled-components';
import type { MouseEvent } from 'react';
import { ArtworkObjectImage } from '../artwork/ArtworkObjectImage';
import { MenuDropdown } from '../ui';
import type { MenuItem } from '../ui/MenuDropdown';

interface Artwork {
  artwork_id: number;
  student_id?: number | null;
  tags?: string[];
  uploaded_at: string;
  updated_at?: string;
}

interface ArtworkGridProps {
  artworks: Artwork[];
  viewMode: 'grid' | 'list';
  selectedArtworks: Set<number>;
  showCheckboxes: boolean;
  hoveredArtwork: number | null;
  activeMenu: number | null;
  onHover: (artworkId: number | null) => void;
  onSelect: (artworkId: number, selected: boolean) => void;
  onMenuToggle: (artworkId: number | null) => void;
  onViewImage: (artworkId: number) => void;
  onEditArtwork: (artworkId: number) => void;
  onDeleteArtwork: (artworkId: number) => void;
  onRotateArtwork: (artworkId: number, direction: 'clockwise' | 'counterclockwise') => void;
  getStudentName: (studentId?: number | null) => string;
  formatRelativeDate: (dateString: string) => string;
}

const Grid = styled.div<{ $viewMode: 'grid' | 'list' }>`
  display: grid;
  gap: ${({ theme }) => theme.spacing.lg};
  padding: ${({ theme }) => theme.spacing.lg};

  ${({ $viewMode, theme }) => $viewMode === 'grid' ? `
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));

    @media (max-width: ${theme.breakpoints.tablet}) {
      grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
      gap: ${theme.spacing.md};
    }

    @media (max-width: ${theme.breakpoints.mobile}) {
      grid-template-columns: 1fr 1fr;
      gap: ${theme.spacing.sm};
    }
  ` : `
    grid-template-columns: 1fr;
  `}
`;

const ArtworkCard = styled.div<{ $viewMode: 'grid' | 'list'; $selected?: boolean }>`
  background: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: visible;
  transition: all ${({ theme }) => theme.transitions.normal};
  border: 2px solid ${({ $selected, theme }) => $selected ? theme.colors.primary : 'transparent'};
  cursor: pointer;
  position: relative;

  ${({ $viewMode, theme }) => $viewMode === 'grid' ? `
    display: flex;
    flex-direction: column;

    &:hover {
      transform: translateY(-2px);
      box-shadow: ${theme.shadows.lg};
    }
  ` : `
    display: grid;
    grid-template-columns: auto 80px 1fr auto;
    align-items: center;
    gap: ${theme.spacing.md};
    padding: ${theme.spacing.md};

    &:hover {
      background: ${theme.colors.gray.light};
    }
  `}
`;

const ThumbnailContainer = styled.div`
  position: relative;
  overflow: hidden;
`;

const CheckboxOverlay = styled.div<{ $visible: boolean; $checked: boolean }>`
  position: absolute;
  top: 8px;
  left: 8px;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $checked, theme }) => $checked ? theme.colors.primary : 'rgba(255, 255, 255, 0.9)'};
  border: 2px solid ${({ $checked, theme }) => $checked ? theme.colors.primary : theme.colors.gray.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  opacity: ${({ $visible }) => $visible ? 1 : 0};
  visibility: ${({ $visible }) => $visible ? 'visible' : 'hidden'};
  transform: ${({ $visible }) => $visible ? 'scale(1)' : 'scale(0.8)'};
  transition: all ${({ theme }) => theme.transitions.fast};
  cursor: pointer;
  z-index: 10;

  &:hover {
    transform: ${({ $visible }) => $visible ? 'scale(1.05)' : 'scale(0.8)'};
  }
`;

const CheckboxIcon = styled.div<{ $checked: boolean }>`
  width: 18px;
  height: 18px;
  color: ${({ $checked, theme }) => $checked ? theme.colors.white : theme.colors.text};
  font-size: 14px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SelectionOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${({ theme }) => theme.colors.primary}20;
  border: 2px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  pointer-events: none;
`;

const ListCheckboxCell = styled.div<{ $visible: boolean }>`
  opacity: ${({ $visible }) => $visible ? 1 : 0};
  visibility: ${({ $visible }) => $visible ? 'visible' : 'hidden'};
  transition: all ${({ theme }) => theme.transitions.fast};
  padding-right: ${({ theme }) => theme.spacing.sm};
`;

const StyledCheckbox = styled.input.attrs({ type: 'checkbox' })`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: ${({ theme }) => theme.colors.primary};
`;

const ArtworkInfo = styled.div<{ $viewMode: 'grid' | 'list' }>`
  padding: ${({ $viewMode, theme }) => $viewMode === 'grid' ? theme.spacing.md : '0'};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const StudentName = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const TagsPreview = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const TagChip = styled.span`
  background: ${({ theme }) => theme.colors.primary}20;
  color: ${({ theme }) => theme.colors.primary};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

const UploadDate = styled.div`
  color: ${({ theme }) => theme.colors.gray.dark};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
`;

const ActionsMenu = styled.div<{ $viewMode?: 'grid' | 'list' }>`
  position: ${({ $viewMode }) => $viewMode === 'grid' ? 'absolute' : 'relative'};
  ${({ $viewMode }) => $viewMode === 'grid' ? `
    bottom: 8px;
    right: 8px;
  ` : ''}
  display: inline-block;
  overflow: visible;
  z-index: 1900;
`;

function AdminArtworkImage({
  artwork,
  viewMode,
  alt,
  onClick,
}: {
  artwork: Artwork;
  viewMode: 'grid' | 'list';
  alt: string;
  onClick: (event: MouseEvent<HTMLImageElement>) => void;
}) {
  return (
    <ArtworkObjectImage
      artworkId={artwork.artwork_id}
      variant="thumbnail"
      updatedAt={artwork.updated_at}
      access="admin"
      alt={alt}
      onClick={onClick}
      style={{
        width: '100%',
        height: viewMode === 'grid' ? '150px' : '80px',
        objectFit: 'cover',
        background: 'var(--gallery-gray-light, #f1f1f1)',
      }}
    />
  );
}

export function ArtworkGrid({
  artworks,
  viewMode,
  selectedArtworks,
  showCheckboxes,
  hoveredArtwork,
  activeMenu,
  onHover,
  onSelect,
  onMenuToggle,
  onViewImage,
  onEditArtwork,
  onDeleteArtwork,
  onRotateArtwork,
  getStudentName,
  formatRelativeDate,
}: ArtworkGridProps) {
  const getMenuItems = (artworkId: number): MenuItem[] => [
    { label: 'View Full Size', icon: '👁️', onClick: () => onViewImage(artworkId) },
    { label: 'Edit Details', icon: '✏️', onClick: () => onEditArtwork(artworkId) },
    { label: 'Quick Tag', icon: '🏷️', onClick: () => onEditArtwork(artworkId) },
    { label: 'Rotate Right', icon: '🔄', onClick: () => onRotateArtwork(artworkId, 'clockwise') },
    { label: 'Rotate Left', icon: '🔄', onClick: () => onRotateArtwork(artworkId, 'counterclockwise') },
    { label: 'Delete', icon: '🗑️', onClick: () => onDeleteArtwork(artworkId), variant: 'danger' as const },
  ];

  return (
    <Grid $viewMode={viewMode}>
      {artworks.map((artwork) => {
        const isSelected = selectedArtworks.has(artwork.artwork_id);
        const isHovered = hoveredArtwork === artwork.artwork_id;
        const showCheckbox = showCheckboxes || isHovered;

        return (
          <ArtworkCard
            key={artwork.artwork_id}
            $viewMode={viewMode}
            $selected={isSelected}
            onMouseEnter={() => onHover(artwork.artwork_id)}
            onMouseLeave={() => onHover(null)}
          >
            {viewMode === 'grid' ? (
              <>
                <ThumbnailContainer>
                  <AdminArtworkImage
                    artwork={artwork}
                    alt={`Artwork by ${getStudentName(artwork.student_id)}`}
                    viewMode={viewMode}
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewImage(artwork.artwork_id);
                    }}
                  />

                  <CheckboxOverlay
                    $visible={showCheckbox}
                    $checked={isSelected}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelect(artwork.artwork_id, !isSelected);
                    }}
                    role="checkbox"
                    aria-checked={isSelected}
                    aria-label={`Select artwork by ${getStudentName(artwork.student_id)}`}
                  >
                    <CheckboxIcon $checked={isSelected}>
                      {isSelected ? '✓' : ''}
                    </CheckboxIcon>
                  </CheckboxOverlay>

                  {isSelected && <SelectionOverlay />}
                </ThumbnailContainer>

                <ArtworkInfo $viewMode={viewMode}>
                  <StudentName>{getStudentName(artwork.student_id)}</StudentName>

                  {artwork.tags && artwork.tags.length > 0 && (
                    <TagsPreview>
                      {artwork.tags.slice(0, 3).map((tag, index) => (
                        <TagChip key={index}>{tag}</TagChip>
                      ))}
                      {artwork.tags.length > 3 && (
                        <TagChip>+{artwork.tags.length - 3} more</TagChip>
                      )}
                    </TagsPreview>
                  )}

                  <UploadDate>{formatRelativeDate(artwork.uploaded_at)}</UploadDate>
                </ArtworkInfo>

                {!showCheckboxes && (
                  <ActionsMenu $viewMode={viewMode}>
                    <MenuDropdown
                      isOpen={activeMenu === artwork.artwork_id}
                      onClose={() => onMenuToggle(null)}
                      items={getMenuItems(artwork.artwork_id)}
                      trigger={<span>⋮</span>}
                      onTriggerClick={() => onMenuToggle(activeMenu === artwork.artwork_id ? null : artwork.artwork_id)}
                    />
                  </ActionsMenu>
                )}
              </>
            ) : (
              <>
                <ListCheckboxCell $visible={showCheckboxes}>
                  <StyledCheckbox
                    checked={isSelected}
                    onChange={(e) => {
                      e.stopPropagation();
                      onSelect(artwork.artwork_id, e.target.checked);
                    }}
                    aria-label={`Select artwork by ${getStudentName(artwork.student_id)}`}
                    tabIndex={showCheckboxes ? 0 : -1}
                  />
                </ListCheckboxCell>

                <AdminArtworkImage
                  artwork={artwork}
                  alt={`Artwork by ${getStudentName(artwork.student_id)}`}
                  viewMode={viewMode}
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewImage(artwork.artwork_id);
                  }}
                />

                <ArtworkInfo $viewMode={viewMode}>
                  <StudentName>{getStudentName(artwork.student_id)}</StudentName>

                  {artwork.tags && artwork.tags.length > 0 && (
                    <TagsPreview>
                      {artwork.tags.slice(0, 3).map((tag, index) => (
                        <TagChip key={index}>{tag}</TagChip>
                      ))}
                      {artwork.tags.length > 3 && (
                        <TagChip>+{artwork.tags.length - 3} more</TagChip>
                      )}
                    </TagsPreview>
                  )}

                  <UploadDate>{formatRelativeDate(artwork.uploaded_at)}</UploadDate>
                </ArtworkInfo>

                {!showCheckboxes && (
                  <ActionsMenu $viewMode={viewMode}>
                    <MenuDropdown
                      isOpen={activeMenu === artwork.artwork_id}
                      onClose={() => onMenuToggle(null)}
                      items={getMenuItems(artwork.artwork_id)}
                      trigger={<span>⋮</span>}
                      onTriggerClick={() => onMenuToggle(activeMenu === artwork.artwork_id ? null : artwork.artwork_id)}
                    />
                  </ActionsMenu>
                )}
              </>
            )}
          </ArtworkCard>
        );
      })}
    </Grid>
  );
}

export type { ArtworkGridProps, Artwork };
